import { useEffect, useState } from "react";
import Modal from "react-modal";
import { MdClose, MdKeyboardArrowUp, MdKeyboardArrowDown, MdDragIndicator } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import { FiCheck, FiRotateCcw } from "react-icons/fi";
import styles from "../../styles/navOrder.module.css";

// Lets the admin reorder the dashboard sidebar items (arrows everywhere, drag on desktop)
const NavOrderModal = ({ isOpen, items, defaultItems, onClose, onSave }) => {
  const [list, setList]       = useState(items);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [saving, setSaving]   = useState(false);

  // Start from the current order every time the modal opens
  useEffect(() => {
    if (isOpen) setList(items);
  }, [isOpen, items]);

  const move = (from, to) => {
    if (to < 0 || to >= list.length || from === to) return;
    setList((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const handleDrop = (idx) => {
    if (dragIdx !== null) move(dragIdx, idx);
    setDragIdx(null);
    setOverIdx(null);
  };

  const isDefault = list.every((item, i) => item.id === defaultItems[i]?.id);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Saving the default order clears the preference on the server
      await onSave(isDefault ? [] : list.map((item) => item.id));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <div className={styles.head}>
        <span className={styles.title}>ترتيب القائمة</span>
        <button type="button" className={styles.close} onClick={onClose} aria-label="إغلاق">
          <MdClose />
        </button>
      </div>

      <p className={styles.hint}>استخدم الأسهم أو اسحب العناصر لتغيير ترتيب القائمة الجانبية.</p>

      <ul className={styles.list}>
        {list.map((item, i) => (
          <li
            key={item.id}
            className={`${styles.row} ${dragIdx === i ? styles.dragging : ""} ${overIdx === i && dragIdx !== i ? styles.over : ""}`}
            draggable
            onDragStart={(e) => {
              setDragIdx(i);
              e.dataTransfer.effectAllowed = "move";
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (overIdx !== i) setOverIdx(i);
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(i);
            }}
            onDragEnd={() => {
              setDragIdx(null);
              setOverIdx(null);
            }}
          >
            <MdDragIndicator className={styles.grip} />
            <span className={styles.num}>{i + 1}</span>
            <item.icon className={styles.icon} />
            <span className={styles.label}>{item.label}</span>
            <div className={styles.arrows}>
              <button type="button" className={styles.arrow} onClick={() => move(i, i - 1)} disabled={i === 0} aria-label="لأعلى">
                <MdKeyboardArrowUp />
              </button>
              <button type="button" className={styles.arrow} onClick={() => move(i, i + 1)} disabled={i === list.length - 1} aria-label="لأسفل">
                <MdKeyboardArrowDown />
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className={styles.actions}>
        <button type="button" className={styles.save} onClick={handleSave} disabled={saving}>
          {saving ? <ImSpinner2 className={styles.spin} /> : <><FiCheck /> حفظ</>}
        </button>
        <button type="button" className={styles.reset} onClick={() => setList(defaultItems)} disabled={isDefault || saving}>
          <FiRotateCcw /> الترتيب الافتراضي
        </button>
        <button type="button" className={styles.cancel} onClick={onClose} disabled={saving}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
};

export default NavOrderModal;
